import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import Link from '~/src/components/link/Link';
import ActionBubble from '~/src/components/actionBubble/ActionBubble';
import TeaserAction from '~/src/components/teaser/teaserAction/TeaserAction';
import NavFriday from '~/src/components/svg/navigation/NavFriday';
import LematinLogo from '~/src/components/svg/external/LematinLogo';
import SectionContextProvider from '~/src/components/context/SectionContext';
import TypeContext from '~/src/components/context/TypeContext';
import TeaserContentMedia from '~/src/components/teaser/content/media/TeaserContentMedia';
import TeaserContentHeader from '~/src/components/teaser/content/header/TeaserContentHeader';

import {classNames, getFormatClassNames} from '~/src/utilities/helpers/css';
import {TeaserFormat, ActionBubbleFormat, ColorScheme} from '~/src/utilities/constants/formats';
import {getContentUrl, getVideoUrl} from '~/src/utilities/helpers/link';
import {withSectionContext} from '~/src/components/context/SectionContext';
import {SectionLayout} from '~/src/utilities/constants/layout';
import {ContentStyle} from '~/src/utilities/constants/style';
import {TeaserTypes} from '~/src/utilities/constants/types';
import {SiteArea} from '~/src/utilities/constants/navigation';
import {trackEvent, registerEvent} from '~/src/utilities/tracking/beagle';
import {trackGtm} from '~/src/utilities/tracking/gtm';
import {BeagleEvents, GtmEvents} from '~/src/utilities/constants/tracking';
import {trackEvent as trackAnaEvent} from '~/src/utilities/helpers/analytics';
import {withSettingsContext} from '~/src/components/context/SettingsContext';

import css from './TeaserContent.module.scss';

const TeaserContent = (props) => {
    const isBreakingNews = props.type === TeaserTypes.BREAKINGNEWS || get(props, 'content.settings.breakingNews');

    const siteArea = get(props, 'context.settings.siteArea', SiteArea.NEWS);

    const isFridaySection = get(props, 'context.section.layout') === SectionLayout.FRIDAY || get(props, 'content.styles.name') === ContentStyle.FRIDAY;
    const isLematinSection = get(props, 'context.section.layout') === SectionLayout.LEMATIN || get(props, 'content.styles.name') === ContentStyle.LEMATIN;
    const isLifestyleSection = get(props, 'context.section.layout') === SectionLayout.LIFESTYLE || get(props, 'content.styles.name') === ContentStyle.LIFESTYLE;
    const isCarouselSection =
        get(props, 'context.section.layout') === SectionLayout.CAROUSEL || get(props, 'context.section.layout') === SectionLayout.CAROUSEL_VIDEO_SERIES;
    const isCategorySlider = get(props, 'context.section.carouselType') === 'categorySlider';
    const isVideoTeaser = props.type === TeaserTypes.VIDEOS;
    const isTopList = props.format.primaryGrid === TeaserFormat.TEASER_TOP_LIST;
    const isLink = props.type === TeaserTypes.LINKS;

    const {isSelected} = props;
    const className = classNames(css, 'teaserContent', getFormatClassNames(props.format), props.type, siteArea, {
        isFridaySection,
        isLematinSection,
        isLifestyleSection,
        isCarouselSection,
        isSelected,
        isBreakingNews
    });

    const target = get(props.content, 'openInNewWindow') ? '_blank' : null;

    const showMedia = !isTopList;
    const showOrdinal = isTopList;
    const showTitleHeader = props.content.titleHeader && !isLematinSection;
    const showLead =
        props.content.lead &&
        !isVideoTeaser &&
        !isFridaySection &&
        !isLematinSection &&
        !isLifestyleSection &&
        siteArea !== SiteArea.VIDEO &&
        siteArea !== SiteArea.LIFESTYLE &&
        props.showLead;
    const showFooter =
        (props.type !== TeaserTypes.LINKS &&
            props.type !== TeaserTypes.EMBEDS &&
            !isFridaySection &&
            !isLematinSection &&
            (!isCarouselSection || (isCarouselSection && isCategorySlider)) &&
            !isBreakingNews) ||
        (siteArea === SiteArea.NEWS && props.type !== TeaserTypes.LINK && props.layout === SectionLayout.CAROUSEL);

    const openCommentPage = props.type === TeaserTypes.SLIDESHOWS || props.type === TeaserTypes.TICKERS;
    const contentUrl = isVideoTeaser ? getVideoUrl(props.content.url) : getContentUrl(props.content.url);

    const trackTeaserClick = (event) => {
        if (isLink) {
            trackAnaEvent({action: 'visit', contentId: props.id, contentType: 'link'});
        }

        if (target !== '_blank') {
            if (isVideoTeaser) {
                registerEvent(BeagleEvents.OPEN_VIDEO, {
                    category: get(props, 'content.mainCategoryFullUrlPath', ''),
                    resourcePositionRowNum: props.sortID,
                    resourcePositionPage: `videoPortal${
                        props.layout === SectionLayout.RELATED_CONTENT && props.trackingData ? props.trackingData.uid : props.context.section.title
                    }`,
                    trackingData: props.trackingData
                });

                if (isCarouselSection) {
                    trackGtm(GtmEvents.VIDEO_SLIDER, props);
                }
            } else {
                trackEvent(BeagleEvents.OPEN_STORY, {
                    category: get(props, 'content.mainCategoryFullUrlPath', ''),
                    resourceId: props.id,
                    resourcePositionRowNum: props.sortID,
                    resourcePositionPage:
                        props.layout === SectionLayout.RELATED_CONTENT && props.trackingData ? props.trackingData.uid : props.context.section.title,
                    trackingData: props.trackingData
                });
            }
        }
    };

    return (
        <TypeContext value={props.type}>
            <Link className={className} href={contentUrl} target={target} onClick={trackTeaserClick}>
                {showMedia && <TeaserContentMedia {...props} isBreakingNews={isBreakingNews} />}

                <section className={css.content}>
                    <TeaserContentHeader
                        showTitleHeader={showTitleHeader}
                        titleHeader={get(props, 'content.titleHeader')}
                        title={get(props, 'content.title')}
                        showLead={showLead}
                        showOrdinal={showOrdinal}
                        lead={get(props, 'content.lead')}
                        isBreakingNews={isBreakingNews}
                        styleName={get(props, 'content.styles.name')}
                        format={props.format}
                        type={props.type}
                        sortID={props.sortID}
                    />

                    {isFridaySection && (
                        <div className={css.fridayLogo}>
                            <NavFriday />
                        </div>
                    )}

                    {isLematinSection && (
                        <div className={css.lematinLogo}>
                            <LematinLogo />
                        </div>
                    )}

                    {showFooter && (
                        <footer className={css.footer}>
                            <TeaserAction
                                id={props.id}
                                likes={get(props, 'clientdata.community.likes')}
                                shares={get(props, 'clientdata.community.shares')}
                                comments={get(props, 'clientdata.community.comments')}
                                contentRatings={get(props, 'clientdata.community.contentRatings')}
                                settings={get(props, 'content.settings')}
                                openCommentPage={openCommentPage}
                                showNumbers={props.layout !== SectionLayout.RELATED_CONTENT}
                                format={props.format}
                                styles={get(props, 'content.styles')}
                            />
                            <ActionBubble
                                metadataId={props.id}
                                format={ActionBubbleFormat.ACTION_BUBBLE_TEASER}
                                teaserFormat={props.format}
                                settings={get(props, 'content.settings')}
                                openCommentPage={openCommentPage}
                                styles={get(props, 'content.styles')}
                            />
                        </footer>
                    )}
                </section>
            </Link>
        </TypeContext>
    );
};

if (process.env.NODE_ENV === 'development') {
    TeaserContent.propTypes = {
        type: PropTypes.string.isRequired,
        openInNewWindow: PropTypes.bool,
        content: PropTypes.shape({
            title: PropTypes.string.isRequired,
            lead: PropTypes.string.isRequired,
            useEmbedUrl: PropTypes.bool,
            image: PropTypes.shape({
                id: PropTypes.string.isRequired,
                variants: PropTypes.objectOf(
                    PropTypes.shape({
                        src: PropTypes.string.isRequired,
                        width: PropTypes.number.isRequired,
                        height: PropTypes.number.isRequired
                    })
                )
            }),
            kickword: PropTypes.shape({
                name: PropTypes.string.isRequired,
                colorCode: PropTypes.string,
                backgroundColorCode: PropTypes.string,
                textColorCode: PropTypes.string,
                borderColorCode: PropTypes.string
            }),
            settings: PropTypes.shape({
                commentingEnabled: PropTypes.bool.isRequired,
                sharingEnabled: PropTypes.bool.isRequired
            }),
            styles: PropTypes.shape({
                name: PropTypes.string.isRequired
            }),
            url: PropTypes.string
        }),
        id: PropTypes.string.isRequired,
        feed: PropTypes.string.isRequired,
        feedArticle: PropTypes.string,
        sortID: PropTypes.number,
        format: PropTypes.shape({
            primaryGrid: PropTypes.oneOf(Object.values(TeaserFormat)).isRequired,
            secondaryGrid: PropTypes.oneOf(Object.values(TeaserFormat)).isRequired,
            colorScheme: PropTypes.oneOf(Object.values(ColorScheme)).isRequired
        }),
        context: PropTypes.shape({
            section: SectionContextProvider.propTypes.value
        })
    };
}

export default withSettingsContext(withSectionContext(TeaserContent));
